import { isUndefined } from 'lodash';

export const columnItemSeparator = ',';
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Flexibility to unflatten any field
export const unflattenObject = (obj: any) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Flexibility to unflatten any field
  const result: any = {};
  for (const i in obj) {
    if (!obj.hasOwnProperty(i)) {
      continue;
    }
    const keys = i.split('.');
    let memo = result;
    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        memo[key] = obj[i];
      } else {
        memo[key] = memo[key] || {};
      }
      memo = memo[key];
    });
  }
  return result;
};

export const findCommonItems = <T>(
  allArrays: (T[] | undefined)[],
  compareBy: ((item: T) => unknown) | keyof T
): T[] | undefined => {
  // Returns undefined if all arrays are undefined
  if (allArrays.every((array) => isUndefined(array))) {
    return undefined;
  }

  // Returns empty if any arrays are empty or undefined
  if (allArrays.some((array) => isUndefined(array) || array.length === 0)) {
    return [];
  }

  // Comparison function
  const getCompareValue = typeof compareBy === 'function' ? compareBy : (item: T) => item[compareBy];

  return allArrays[0]?.filter((item) =>
    allArrays
      .slice(1)
      .every((array) => array?.some((otherItem) => getCompareValue(item) === getCompareValue(otherItem)))
  );
};

/**
 * Checks if a value is a primitive type (string, number, or boolean).
 */
const isPrimitive = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

/**
 * Recursively flattens an object into dot-notation keys, converting arrays to comma-separated strings.
 * This is useful for preparing nested data structures for display in data grids or tables.
 *
 * @param value - The value to flatten (can be primitive, object, or array)
 * @param key - The current key path (dot-notation)
 * @param result - The accumulator object for flattened key-value pairs
 *
 * @example
 * const data = { user: { name: 'John', tags: ['admin', 'user'] }, active: true };
 * const result = {};
 * flattenObjectAndConvertArrayToCSV(data, 'data', result);
 * // result = { 'data.user.name': 'John', 'data.user.tags': 'admin, user', 'data.active': true }
 */
export const flattenObjectAndConvertArrayToCSV = (
  value: unknown,
  key: string,
  result: Record<string, string | number | boolean>
): void => {
  if (value == null) {
    return;
  }

  if (Array.isArray(value)) {
    const filtered = value.filter((v) => v != null);
    if (filtered.length === 0) {
      return;
    }

    result[key] =
      typeof filtered[0] === 'object'
        ? filtered.map((item) => JSON.stringify(item)).join(`${columnItemSeparator} `)
        : filtered.join(`${columnItemSeparator} `);
    return;
  }

  if (isPrimitive(value)) {
    result[key] = value;
    return;
  }

  if (typeof value === 'object') {
    for (const [nestedKey, nestedValue] of Object.entries(value as Record<string, unknown>)) {
      flattenObjectAndConvertArrayToCSV(nestedValue, `${key}.${nestedKey}`, result);
    }
  }
};

/**
 * Flattens an array of objects by extracting all fields and converting them to comma-separated strings.
 * Each field from the objects becomes a separate key with all values joined by commas.
 *
 * @param value - The array value to process
 * @param keyPrefix - The key prefix to use for the flattened results
 * @param result - The accumulator object for flattened key-value pairs
 *
 * @example
 * const accounts = [
 *   { tag: 'accountID', value: 'ACCT-123', accountType: 'IRA' },
 *   { tag: 'accountID', value: 'ACCT-456', accountType: 'Roth IRA' }
 * ];
 * const result = {};
 * flattenArrayWithFieldExtraction(accounts, 'metadata.accounts', result);
 * // result = {
 * //   'metadata.accounts.tag': 'accountID, accountID',
 * //   'metadata.accounts.value': 'ACCT-123, ACCT-456',
 * //   'metadata.accounts.accountType': 'IRA, Roth IRA'
 * // }
 */
export const flattenArrayWithFieldExtraction = (
  value: unknown,
  keyPrefix: string,
  result: Record<string, string | number | boolean>
): void => {
  if (!Array.isArray(value)) {
    return;
  }

  const filtered = value.filter((v) => v != null && typeof v === 'object');
  if (filtered.length === 0) {
    return;
  }

  const arrayOfObjects = filtered as Array<Record<string, unknown>>;
  const fields = Object.keys(arrayOfObjects[0]);

  if (fields.length === 0) {
    return;
  }

  for (const fieldName of fields) {
    const extractedValues = arrayOfObjects
      .map((item) => item[fieldName])
      .filter(isPrimitive)
      .map(String);

    if (extractedValues.length > 0) {
      result[`${keyPrefix}.${fieldName}`] = extractedValues.join(`${columnItemSeparator} `);
    }
  }
};
